import React from "react";

import { PortableTextComponents } from "@portabletext/react";

import { Abbreviation } from "components/abbreviation";
import { Typography } from "components/typography";

const getTextFromNode = (node: React.ReactNode): string => {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getTextFromNode).join("");
  if (React.isValidElement(node)) {
    const props = (node.props as { children?: React.ReactNode }) ?? {};
    return props.children ? getTextFromNode(props.children) : "";
  }
  return "";
};

const normalizeLang = (raw: unknown): string | undefined => {
  if (typeof raw !== "string") return undefined;
  const value = raw.trim().toLowerCase();
  if (!value) return undefined;

  const primary = value.split("-")[0];
  const mapped =
    primary === "english"
      ? "en"
      : primary === "swedish"
        ? "sv"
        : primary === "norwegian"
          ? "nb"
          : primary === "danish"
            ? "da"
            : primary === "french"
              ? "fr"
              : primary === "japanese"
                ? "ja"
                : primary;

  const allowed = new Set(["da", "en", "fr", "ja", "nb", "sv"]);
  if (!allowed.has(mapped)) return undefined;
  return mapped;
};

export const inlineSerializer: PortableTextComponents = {
  block: {
    normal: ({ children }) => <Typography>{children}</Typography>,
    h2: ({ children }) => <Typography variant="h2">{children}</Typography>,
    h3: ({ children }) => <Typography variant="h3">{children}</Typography>,
    h4: ({ children }) => <Typography variant="h4">{children}</Typography>,
    preamble: ({ children }) => (
      <Typography variant="preamble">{children}</Typography>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="mt-3 list-disc pl-[0.5em] xs:pl-[1.55em]">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="mt-3 list-decimal pl-[0.5em] xs:pl-[1.55em]">
        {children}
      </ol>
    ),
  },
  marks: {
    link: ({ children, value }) => {
      const rel =
        value.href && !value.href.startsWith("/")
          ? "noreferrer noopener"
          : undefined;
      return (
        <Typography href={value.href} rel={rel} variant="link">
          {children}
        </Typography>
      );
    },
    abbreviation: ({ children, value }) => {
      const abbrText = getTextFromNode(children);
      if (!abbrText || !value?.title) return <>{children}</>;
      return (
        <Abbreviation abbr={abbrText} lang={value?.lang} title={value.title} />
      );
    },
    language: ({ children, value }) => {
      const lang = normalizeLang(value?.lang ?? value?.language);
      if (!lang) return <>{children}</>;
      return <span lang={lang}>{children}</span>;
    },
  },
};
